import React, { useState } from 'react';
import { Box, Typography, Paper, IconButton, Menu, MenuItem, Checkbox, Button } from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { StructuredWorkoutIcon } from '../icons'; // Import the StructuredWorkoutIcon
import styles from './NutritionWidget.module.css';
import { NutritionData, CaloriesStats, MacroDetail } from '../../types/dashboard.types';
import { CalendarEventDto } from '../../types/calendar';
import { AddEventButtons } from '../common/AddEventButtons';

const StyledPaper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '16px',
  gap: '16px',
  width: '100%',
  background: '#FFFFFF',
  boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.05)',
  borderRadius: '12px',
}));

const EventLayout = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: 0,
  gap: '8px',
  width: '100%',
}));

const EventContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  gap: '1px',
  flexGrow: 1,
}));

interface FoodItemProps {
  event: CalendarEventDto;
  onEdit: (event: CalendarEventDto) => void;
  onDelete: (eventId: number) => void;
  onToggleComplete: (eventId: number, isCompleted: boolean) => void;
  onView: (event: CalendarEventDto) => void;
}

const FoodItem: React.FC<FoodItemProps> = ({ event, onEdit, onDelete, onToggleComplete, onView }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const canEdit = event.actions.includes('edit');
  const canDelete = event.actions.includes('delete');
  const canComplete = event.actions.includes('complete');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(event);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(event.id);
    handleClose();
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onToggleComplete(event.id, e.target.checked);
  };

  const formattedTime = new Date(event.scheduledLdt || '').toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
  const displayText = event.title;
  const calories = (event.details as any)?.calc?.cal; // Access calc.cal from details

  return (
    <EventLayout>
      <Checkbox checked={event.isCompleted} onChange={handleToggle} disabled={!canComplete} />
      <EventContent onClick={() => onView(event)} sx={{ cursor: 'pointer' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#282828', fontWeight: 700, fontSize: '15px', lineHeight: '24px' }}>
            {displayText}
          </Typography>
          {event.isStructured && (
            <StructuredWorkoutIcon fontSize="small" sx={{ color: '#6EB52C' }} />
          )}
          {calories && (
            <Typography variant="body2" sx={{ color: '#6EB52C', fontWeight: 600, fontSize: '13px', lineHeight: '24px' }}>
              {calories}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" sx={{ color: '#A8A8A8', fontWeight: 600, fontSize: '13px', lineHeight: '16px' }}>
          {formattedTime}
        </Typography>
      </EventContent>
      {(canEdit || canDelete) && (
        <>
          <IconButton size="small" sx={{ color: '#D3D3D3' }} onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            {canEdit && <MenuItem onClick={handleEdit}>Редагувати</MenuItem>}
            {canDelete && <MenuItem onClick={handleDelete}>Видалити</MenuItem>}
          </Menu>
        </>
      )}
    </EventLayout>
  );
};

interface NutritionWidgetProps {
  nutritionSummary: NutritionData;
  foodEvents: CalendarEventDto[];
  onAddFood?: () => void;
  onQuickAddFood?: () => void;
  onEditFood?: (event: CalendarEventDto) => void;
  onDeleteFood?: (eventId: number) => void;
  onToggleFoodComplete?: (eventId: number, isCompleted: boolean) => void;
  onViewFood?: (event: CalendarEventDto) => void;
}

export const NutritionWidget: React.FC<NutritionWidgetProps> = ({
  nutritionSummary,
  foodEvents,
  onAddFood,
  onQuickAddFood,
  onEditFood,
  onDeleteFood,
  onToggleFoodComplete,
  onViewFood,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  console.log('NutritionWidget - Received nutritionSummary:', nutritionSummary);
  console.log('NutritionWidget - nutritionSummary.calories.goal:', nutritionSummary.calories.goal);
  console.log('NutritionWidget - nutritionSummary.protein.goal:', nutritionSummary.protein.goal);

  const defaultCaloriesStats: CaloriesStats = { goal: 0, actual: 0, percentage: 0, remaining: 0 };
  const defaultMacroDetail: MacroDetail = { actual: 0, goal: 0, percentage: 0, remaining: 0 };

  const safeCalories = nutritionSummary.calories || defaultCaloriesStats;
  const safeProtein = nutritionSummary.protein || defaultMacroDetail;
  const safeFat = nutritionSummary.fat || defaultMacroDetail;
  const safeCarbs = nutritionSummary.carbs || defaultMacroDetail;

  // Helper to calculate percentage, capped at 100% for display width
  const calculateDisplayPercentage = (actual: number, goal: number): number => {
    if (goal === 0) return actual > 0 ? 100 : 0; // Avoid division by zero, if actual > 0 and goal is 0, show 100%
    return Math.min(100, (actual / goal) * 100);
  };

  // Helper to calculate actual percentage (can be > 100%) for color logic
  const calculateActualPercentage = (actual: number, goal: number): number => {
    if (goal === 0) return actual > 0 ? 100 : 0;
    return (actual / goal) * 100;
  };

  const caloriesDisplayPercentage = calculateDisplayPercentage(safeCalories.actual, safeCalories.goal);
  const proteinDisplayPercentage = calculateDisplayPercentage(safeProtein.actual, safeProtein.goal);
  const carbsDisplayPercentage = calculateDisplayPercentage(safeCarbs.actual, safeCarbs.goal);
  const fatsDisplayPercentage = calculateDisplayPercentage(safeFat.actual, safeFat.goal);

  const proteinColorPercentage = calculateActualPercentage(safeProtein.actual, safeProtein.goal);
  const carbsColorPercentage = calculateActualPercentage(safeCarbs.actual, safeCarbs.goal);
  const fatsColorPercentage = calculateActualPercentage(safeFat.actual, safeFat.goal);

  const getMacroProgressBarColor = (percentage: number) => {
    let hue;
    const saturation = 100;
    const lightness = 50;

    if (percentage <= 100) {
      hue = 30 + (percentage / 100) * 90;
    } else {
      const normalizedOverPercentage = Math.min((percentage - 100) / 25, 1);
      hue = 120 - normalizedOverPercentage * 120;
    }

    hue = Math.max(0, Math.min(360, hue));

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const proteinBarColor = getMacroProgressBarColor(proteinColorPercentage);
  const carbsBarColor = getMacroProgressBarColor(carbsColorPercentage);
  const fatsBarColor = getMacroProgressBarColor(fatsColorPercentage);

  const handleEdit = (event: CalendarEventDto) => {
    if (onEditFood) onEditFood(event);
  };

  const handleDelete = (eventId: number) => {
    if (onDeleteFood) onDeleteFood(eventId);
  };

  const handleToggleComplete = (eventId: number, isCompleted: boolean) => {
    if (onToggleFoodComplete) {
      onToggleFoodComplete(eventId, isCompleted);
    }
  };

  const handleView = (event: CalendarEventDto) => {
    if (onViewFood) onViewFood(event);
  };

  const displayedEvents = isExpanded ? foodEvents : foodEvents.slice(0, 3);
  const remainingEventsCount = foodEvents.length - displayedEvents.length;

  return (
    <StyledPaper>
      <div className={styles.content}>
        {/* Заголовок */}
        <div className={styles.header}>
          <div className={styles.titleLayout}>
            <div className={styles.iconWrapper}>
              <RestaurantIcon sx={{ color: '#6EB52C' }} />
            </div>
            <span className={styles.titleText}>Харчування</span>
          </div>
        </div>

        {/* Основна секція */}
        <div className={styles.mainSection}>
          {/* Круговий прогрес */}
          <div className={styles.circularProgress}>
            <svg width="148" height="148" viewBox="0 0 148 148">
              <defs>
                <linearGradient id="nutritionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#D3F491" />
                  <stop offset="100%" stopColor="#7FE8A3" />
                </linearGradient>
              </defs>
              {/* Background circle */}
              <circle
                cx="74"
                cy="74"
                r="70"
                fill="none"
                stroke="#F2F2F7"
                strokeWidth="8"
              />
              {/* Progress arc */}
              <circle
                cx="74"
                cy="74"
                r="70"
                fill="none"
                stroke="url(#nutritionGradient)"
                strokeWidth="8"
                strokeDasharray={`${2 * Math.PI * 70 * (caloriesDisplayPercentage / 100)} ${2 * Math.PI * 70}`}
                strokeLinecap="round"
                transform="rotate(-90 74 74)"
              />
            </svg>
            <div className={styles.circularContent}>
              <div className={styles.caloriesValue}>{safeCalories.actual}ккал</div>
              <div className={styles.caloriesGoal}>Ціль: {safeCalories.goal}</div>
            </div>
          </div>

          {/* Макроелементи */}
          <div className={styles.macrosSection}>
            {/* Білки */}
            <div className={styles.macroItem}>
              <div className={styles.macroIcon}>
                <svg width="19" height="17" viewBox="0 0 19 17" fill="none">
                  <circle cx="5" cy="4" r="3.5" stroke="#282828" strokeWidth="1.5" />
                  <circle cx="5" cy="13" r="3.5" fill="#282828" />
                  <circle cx="14" cy="13" r="3.5" stroke="#282828" strokeWidth="1.5" />
                </svg>
              </div>
              <div className={styles.macroLabel}>Білки</div>
              <div className={styles.macroProgress}>
                <div className={styles.macroBar}> {/* Додано div з класом macroBar */}
                  <div
                    className={styles.macroBarFill}
                    style={{ width: `${proteinDisplayPercentage}%`, backgroundColor: proteinBarColor }}
                  />
                </div>
                <div className={styles.macroValues}>
                  <span className={styles.macroActual}>{safeProtein.actual}</span>
                  <span className={styles.macroGoal}>/{safeProtein.goal}</span>
                </div>
              </div>
            </div>

            {/* Вуглеводи */}
            <div className={styles.macroItem}>
              <div className={styles.macroIcon}>
                <svg width="17" height="20" viewBox="0 0 17 20" fill="none">
                  <path d="M3 3L1 17" stroke="#282828" strokeWidth="1.5" />
                  <path d="M3 8L1 12" stroke="#282828" strokeWidth="1.5" />
                  <path d="M11 3L13 17" stroke="#282828" strokeWidth="1.5" />
                  <path d="M11 8L13 12" stroke="#282828" strokeWidth="1.5" />
                  <rect x="7" y="0" width="4" height="8" fill="#282828" />
                </svg>
              </div>
              <div className={styles.macroLabel}>Вуглеводи</div>
              <div className={styles.macroProgress}>
                <div className={styles.macroBar}> {/* Додано div з класом macroBar */}
                  <div
                    className={styles.macroBarFill}
                    style={{ width: `${carbsDisplayPercentage}%`, backgroundColor: carbsBarColor }}
                  />
                </div>
                <div className={styles.macroValues}>
                  <span className={styles.macroActual}>{safeCarbs.actual}</span>
                  <span className={styles.macroGoal}>/{safeCarbs.goal}</span>
                </div>
              </div>
            </div>

            {/* Жири */}
            <div className={styles.macroItem}>
              <div className={styles.macroIcon}>
                <svg width="11" height="16" viewBox="0 0 11 16" fill="none">
                  <path d="M1 0V10" stroke="#282828" strokeWidth="1.8" />
                  <path d="M5.5 0V15" stroke="#282828" strokeWidth="1.8" />
                  <path d="M10 0V6" stroke="#282828" strokeWidth="1.8" />
                </svg>
              </div>
              <div className={styles.macroLabel}>Жири</div>
              <div className={styles.macroProgress}>
                <div className={styles.macroBar}> {/* Додано div з класом macroBar */}
                  <div
                    className={styles.macroBarFill}
                    style={{ width: `${fatsDisplayPercentage}%`, backgroundColor: fatsBarColor }}
                  />
                </div>
                <div className={styles.macroValues}>
                  <span className={styles.macroActual}>{safeFat.actual}</span>
                  <span className={styles.macroGoal}>/{safeFat.goal}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Food Events Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', mt: 2 }}>
        {displayedEvents.length > 0 ? (
          displayedEvents.map((foodItem) => (
            <FoodItem
              key={foodItem.id}
              event={foodItem}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleComplete={handleToggleComplete}
              onView={handleView}
            />
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center', width: '100%' }}>
            Поки що немає доданої їжі.
          </Typography>
        )}
        {foodEvents.length > 3 && (
          <Button onClick={() => setIsExpanded(!isExpanded)} sx={{ mt: 1 }}>
            {isExpanded ? 'Показати менше' : `Показати ще ${remainingEventsCount}...`}
          </Button>
        )}
      </Box>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 'auto', gap: '8px' }}>
        <AddEventButtons
          onAddText={onAddFood}
          showTextButton={true}
          showVoiceButton={false}
          showPhotoButton={false}
          buttonColor="#6EB52C" // Pass the green color for NutritionWidget
        />
        <IconButton 
          onClick={onQuickAddFood}
          sx={{ 
            backgroundColor: '#F2F2F2', 
            borderRadius: '12px', 
            width: '91.67px', 
            height: '56px',
            color: '#6EB52C'
          }}
        >
          <AutoFixHighIcon fontSize="small" />
        </IconButton>
      </Box>
    </StyledPaper>
  );
};
